import { useEffect, useRef, useState } from 'react';
import { JEROME_FOOTER, type Draft, type DraftMode, type DraftPatch } from '@jerome2/shared';
import { api } from '../api.ts';
import { ARM_MS, isArmed, nextArm, type Armed } from '../lib/armed.ts';
import { loadCapabilities } from '../state/app.ts';
import { errorToast, toast } from '../state/toast.ts';
import { Icon } from '../ui/Icon.tsx';
import { Button, IconButton, inputClass } from '../ui/primitives.tsx';

// De editor: nieuw, beantwoorden, allen beantwoorden, doorsturen, of een bestaand concept (van
// Jerome of uit Outlook) bewerken. Het concept bestaat direct als echt Outlook-concept; wijzigingen
// worden 2 seconden na de laatste toetsaanslag bewaard, met hooguit één save tegelijk. Verzenden en
// sluiten wachten tot alles bewaard is; lukt bewaren niet, dan gaat er niets weg. Verzenden vraagt
// twee klikken (de tweede binnen zes seconden); daarna bekijkt Jerome wat er aan zijn concept
// veranderde en leert daarvan.

const TITLES: Record<DraftMode, string> = { new: 'Nieuwe mail', reply: 'Beantwoorden', replyAll: 'Allen beantwoorden', forward: 'Doorsturen' };

export interface EditorProps {
  /** Hoe een nieuw concept ontstaat; bij een bestaand concept (draftId) komt de soort uit het concept zelf. */
  mode?: DraftMode | undefined;
  messageId?: string | undefined;
  /** Bestaand concept (van Jerome of uit de map Concepten) bewerken in plaats van een nieuw concept maken. */
  draftId?: string | undefined;
  onClose: (result: { sent: boolean; draftId: string | null }) => void;
}

const splitAddresses = (s: string) => s.split(/[,;]+/).map((a) => a.trim()).filter(Boolean);
const joinAddresses = (list: Array<{ name: string; address: string }>) => list.map((a) => a.address).join(', ');

type Field = 'to' | 'cc' | 'subject' | 'bodyText';

/** De wijziging voor de vuile velden, met de waarden van nu. */
function patchFor(fields: Iterable<Field>, v: { to: string; cc: string; subject: string; body: string }): DraftPatch {
  const patch: DraftPatch = {};
  for (const f of fields) {
    if (f === 'to') patch.to = splitAddresses(v.to);
    else if (f === 'cc') patch.cc = splitAddresses(v.cc);
    else if (f === 'subject') patch.subject = v.subject.trim() || '(geen onderwerp)';
    else patch.bodyText = v.body;
  }
  return patch;
}

// Adresvelden: op de telefoon het e-mailtoetsenbord, zonder hoofdletters, autocorrectie of
// spellingscontrole. Geen type="email": daarin passen geen lijsten met komma's en werkt selectionStart niet.
const addressInput = { inputMode: 'email', autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false } as const;

export function Editor(props: EditorProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [saving, setSaving] = useState<'idle' | 'wacht' | 'bezig' | 'bewaard'>('idle');
  const [sending, setSending] = useState(false);
  const [armed, setArmed] = useState<Armed | null>(null);
  const [canSend, setCanSend] = useState<boolean | null>(null);
  const dirty = useRef<Set<Field>>(new Set());
  const inflight = useRef<Promise<boolean> | null>(null);
  const saveRef = useRef<() => Promise<boolean>>(() => Promise.resolve(true));
  // Verzenden of verwijderen is begonnen: niet meer bewaren (weer los als dat mislukt).
  const sealed = useRef(false);
  const touched = useRef(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const armTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [attempt, setAttempt] = useState(0);
  const createMode: DraftMode = props.mode ?? 'new';
  const mode: DraftMode = draft?.mode ?? createMode;
  const fromJerome = !!draft && draft.originalText !== null;
  const title = props.draftId ? (fromJerome ? 'Concept van Jerome' : 'Concept') : TITLES[createMode];

  useEffect(() => {
    let cancelled = false;
    setError(null);
    const load = async () => {
      try {
        // Beantwoorden en doorsturen beginnen leeg: de connector zet citaat en handtekening erin.
        const d = props.draftId ? await api<Draft>(`/api/drafts/${encodeURIComponent(props.draftId)}`) : await api<Draft>('/api/drafts', { method: 'POST', body: { mode: createMode, messageId: props.messageId, to: [], cc: [], subject: createMode === 'new' ? '(geen onderwerp)' : '', bodyText: '' } });
        if (cancelled) return;
        setDraft(d);
        setTo(joinAddresses(d.to));
        setCc(joinAddresses(d.cc));
        setSubject(d.subject === '(geen onderwerp)' ? '' : d.subject);
        setBody(d.bodyText.trim() === '' ? '' : d.bodyText);
        setTimeout(() => (createMode === 'new' || createMode === 'forward' ? document.getElementById('editor-to')?.focus() : bodyRef.current?.focus()), 50);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createMode, props.messageId, props.draftId, attempt]);

  // Of versturen kan: zonder het recht Mail.Send bij de connector krijgt Jeroen dat vooraf te zien (één keer per sessie geladen).
  useEffect(() => {
    let cancelled = false;
    void loadCapabilities().then((c) => {
      if (!cancelled && c) setCanSend(c.send);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Hooguit één PATCH tegelijk: een save terwijl er al een loopt, wacht daarop en bewaart daarna via
  // saveRef (met de nieuwste waarden) wat nog vuil is. Mislukt de PATCH, dan worden die velden weer
  // vuil, zodat de volgende save ze alsnog meeneemt. Waar als alles wat vuil was, bewaard is.
  const save = (): Promise<boolean> => {
    if (inflight.current) return inflight.current.then(() => saveRef.current());
    if (sealed.current || !draft || !dirty.current.size) return Promise.resolve(true);
    const fields = [...dirty.current];
    dirty.current.clear();
    setSaving('bezig');
    const run = api<Draft>(`/api/drafts/${encodeURIComponent(draft.id)}`, { method: 'PATCH', body: patchFor(fields, { to, cc, subject, body }) })
      .then(
        (d) => {
          setDraft(d);
          setSaving(dirty.current.size ? 'wacht' : 'bewaard');
          return true;
        },
        (e: unknown) => {
          for (const f of fields) dirty.current.add(f);
          setSaving('wacht');
          setError((e as Error).message);
          return false;
        },
      )
      .finally(() => {
        inflight.current = null;
      });
    inflight.current = run;
    return run;
  };
  saveRef.current = save;

  const mark = (field: Field) => {
    touched.current = true;
    dirty.current.add(field);
    setSaving('wacht');
    if (timer.current) clearTimeout(timer.current);
    // Via saveRef: deze render kent de waarde van de laatste toets nog niet.
    timer.current = setTimeout(() => void saveRef.current(), 2000);
  };
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
      if (armTimer.current) clearTimeout(armTimer.current);
      // Weg zonder Sluiten (andere mail, ander tabblad): wat nog vuil is één keer bewaren.
      if (dirty.current.size) void saveRef.current();
    },
    [],
  );

  const send = async () => {
    if (!draft || sending) return;
    if (timer.current) clearTimeout(timer.current);
    if (armTimer.current) clearTimeout(armTimer.current);
    const step = nextArm(armed, Date.now());
    setArmed(step.state);
    if (!step.fire) {
      // Eerste klik: op scherp zetten en alvast bewaren; de tweede klik binnen zes seconden verstuurt echt.
      armTimer.current = setTimeout(() => setArmed(null), ARM_MS);
      await saveRef.current();
      return;
    }
    setSending(true);
    setError(null);
    try {
      // Eerst de lopende save en de rest; lukt bewaren niet, dan gaat er niets weg.
      if (!(await saveRef.current())) return;
      sealed.current = true;
      const r = await api<{ subject: string; learning: boolean }>(`/api/drafts/${encodeURIComponent(draft.id)}/send`, { method: 'POST' });
      toast(`Verzonden: ${r.subject}`, { tone: 'ok' });
      if (r.learning) toast('Jerome bekijkt wat je aan zijn concept veranderde en leert daarvan', { tone: 'info' });
      props.onClose({ sent: true, draftId: draft.id });
    } catch (e) {
      sealed.current = false;
      setError((e as Error).message);
    } finally {
      setSending(false);
    }
  };

  const close = async () => {
    if (timer.current) clearTimeout(timer.current);
    if (draft && !props.draftId && !touched.current) {
      // Niets getypt: het lege concept weer opruimen.
      await api(`/api/drafts/${encodeURIComponent(draft.id)}`, { method: 'DELETE' }).catch(() => undefined);
      props.onClose({ sent: false, draftId: null });
      return;
    }
    // Eerst de lopende save en de rest, dan pas dicht.
    await saveRef.current();
    props.onClose({ sent: false, draftId: draft?.id ?? null });
  };

  const remove = async () => {
    if (!draft) return props.onClose({ sent: false, draftId: null });
    if (timer.current) clearTimeout(timer.current);
    sealed.current = true;
    try {
      // Een lopende save eerst laten landen, anders komt die na het verwijderen nog binnen.
      await inflight.current;
      await api(`/api/drafts/${encodeURIComponent(draft.id)}`, { method: 'DELETE' });
      toast('Concept verwijderd', { tone: 'info' });
      props.onClose({ sent: false, draftId: null });
    } catch (e) {
      sealed.current = false;
      errorToast(e);
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        void close();
      }
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        void send();
      }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, to, cc, subject, body, armed, sending]);

  const armedNow = isArmed(armed, Date.now());

  return (
    <div className="absolute inset-0 z-20 bg-surface flex flex-col" role="dialog" aria-label={title}>
      <div className="h-12 shrink-0 flex items-center gap-3 px-4 border-b border-line">
        <Icon name={mode === 'forward' ? 'forward' : mode === 'replyAll' ? 'replyAll' : mode === 'new' ? 'pen' : 'reply'} size={17} className="text-ink-soft shrink-0" />
        <span className="font-medium min-w-0 flex-1 truncate">{title}</span>
        <div className="shrink-0 flex items-center gap-2">
          <IconButton icon="trash" label="Concept verwijderen" onClick={() => void remove()} />
          <Button size="sm" onClick={() => void close()}>
            Sluiten <span className="text-muted text-[11px] ml-1">Esc</span>
          </Button>
          <Button variant="primary" size="sm" icon="send" onClick={() => void send()} busy={sending} disabled={!draft} className={armedNow ? 'ring-2 ring-amber ring-offset-1' : ''}>
            {armedNow ? 'Nu verzenden' : 'Verzenden'}
          </Button>
        </div>
      </div>
      {armedNow ? (
        <div className="px-4 py-1.5 bg-amber-soft text-amber text-[13px] truncate" role="status">
          Nog eens klikken verzendt naar {to.trim() || 'de ontvangers'}.
        </div>
      ) : saving !== 'idle' ? (
        <div className="px-4 py-1 text-[12px] text-muted">{saving === 'bezig' ? 'Bewaren…' : saving === 'bewaard' ? 'Bewaard in Outlook' : 'Nog niet bewaard'}</div>
      ) : null}
      {canSend === false ? (
        <div className="px-4 py-2 bg-amber-soft text-ink text-[13px] flex items-start gap-2">
          <Icon name="warning" size={15} className="text-amber shrink-0 mt-0.5" />
          <span>
            Versturen kan nog niet: de mailbox-connector heeft het recht Mail.Send niet. Meld je opnieuw aan bij de connector (of geef in Microsoft Entra admin consent voor Mail.Send); het concept blijft intussen in Outlook staan.
          </span>
        </div>
      ) : null}
      {error && draft ? <div className="px-4 py-2 bg-red-soft text-red text-[13px]">{error}</div> : null}
      {!draft && error ? (
        <div className="p-6 max-w-[560px]">
          <p className="font-medium text-red mb-1">{props.draftId ? 'Het concept kon niet worden geopend.' : 'Het concept kon niet worden aangemaakt.'}</p>
          <p className="text-[14px] text-ink-soft mb-4">{error}</p>
          <div className="flex gap-2">
            <Button variant="primary" icon="refresh" onClick={() => setAttempt((a) => a + 1)}>
              Opnieuw
            </Button>
            <Button onClick={() => props.onClose({ sent: false, draftId: null })}>Sluiten</Button>
          </div>
        </div>
      ) : !draft ? (
        <div className="p-6 text-muted">{props.draftId ? 'Concept ophalen…' : 'Concept aanmaken in Outlook…'}</div>
      ) : draft ? (
        <div className="flex-1 min-h-0 overflow-auto">
          <div className="max-w-[860px] px-6 py-4 space-y-3">
            <label className="flex items-center gap-3">
              <span className="w-20 text-[13px] text-muted">Aan</span>
              <input id="editor-to" value={to} onChange={(e) => { setTo(e.target.value); mark('to'); }} className={inputClass} placeholder="naam@bedrijf.nl, tweede@bedrijf.nl" {...addressInput} />
            </label>
            <label className="flex items-center gap-3">
              <span className="w-20 text-[13px] text-muted">Cc</span>
              <input value={cc} onChange={(e) => { setCc(e.target.value); mark('cc'); }} className={inputClass} {...addressInput} />
            </label>
            <label className="flex items-center gap-3">
              <span className="w-20 text-[13px] text-muted">Onderwerp</span>
              <input value={subject} onChange={(e) => { setSubject(e.target.value); mark('subject'); }} className={inputClass} />
            </label>
            <textarea ref={bodyRef} value={body} onChange={(e) => { setBody(e.target.value); mark('bodyText'); }} rows={14} placeholder="Schrijf je bericht. De handtekening zet de connector er zelf onder." className="w-full rounded-md border border-line-strong px-4 py-3 text-[15px] leading-relaxed focus:border-accent focus:outline-none resize-y" aria-label="Tekst" />
            {fromJerome ? (
              <div className="rounded-lg bg-tint px-4 py-3 text-[13px] text-ink-soft">
                <p>
                  <Icon name="bolt" size={13} className="inline text-accent mr-1" />
                  Dit concept schreef Jerome. Wat je aanpast, bekijkt hij na het versturen om ervan te leren; je ziet dan een melding met wat hij geleerd heeft. Onder de handtekening komt de regel "{JEROME_FOOTER}".
                </p>
                <details className="mt-2">
                  <summary className="cursor-pointer text-ink">Tekst van Jerome</summary>
                  <pre className="mt-2 whitespace-pre-wrap font-sans text-[13px] text-ink-soft">{draft.originalText}</pre>
                </details>
              </div>
            ) : null}
            <p className="text-[12px] text-muted">{mode === 'new' ? 'Verzend met ⌘Enter (twee keer).' : 'Citaat en bijlagen van de oorspronkelijke mail blijven in het concept staan. Verzend met ⌘Enter (twee keer).'}</p>
          </div>
        </div>
      ) : null}
    </div>
  );
}
